import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api, type BankConnection, type SyncRun } from '../../lib/api';

/**
 * Conexoes bancarias.
 *
 * A tela e franca sobre tres coisas que nenhum agregador costuma dizer.
 *
 * A primeira: **o consentimento vence**, em ate 12 meses, e vence em
 * silencio — a conexao simplesmente para de atualizar, sem erro nenhum. Por
 * isso a contagem regressiva fica visivel o tempo todo, e nao so quando ja e
 * tarde.
 *
 * A segunda: **o itemId e colado a mao**. Nao e preguica — `GET /items`
 * responde 403 no plano gratuito, entao nao ha como listar os consentimentos
 * pelo lado de ca. Dizer isso e melhor do que deixar a pessoa procurando um
 * botao que nao existe.
 *
 * A terceira: **cada varredura presta contas**. Quantos vieram, quantos
 * entraram, quantos ja existiam e quantos nao deram para traduzir. "Nao veio
 * nada" e "falhou calado" precisam ser distinguiveis na tela, nao so no log.
 */
export function ConnectionsPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [addingCredential, setAddingCredential] = useState(false);
  const [addingConnection, setAddingConnection] = useState(false);
  const [openRuns, setOpenRuns] = useState<string | null>(null);

  const credentials = useQuery({
    queryKey: ['openfinance', 'credenciais'],
    queryFn: () => api.openFinanceCredentials(),
  });

  const connections = useQuery({
    queryKey: ['openfinance', 'conexoes'],
    queryFn: () => api.bankConnections(),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['openfinance'] });
    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const sync = useMutation({
    mutationFn: (id: string) => api.syncBankConnection(id),
    onSuccess: ({ run }) => {
      setError(null);
      setNotice(resumoDaVarredura(run));
      refresh();
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  /**
   * Releitura do historico.
   *
   * Separada de "Sincronizar agora" de proposito, e nao escondida dentro
   * dela: pede a Pluggy a janela inteira, demora bem mais e nao e o que se
   * quer no dia a dia. Existe para quando o sistema passa a guardar algo que
   * antes descartava e os lancamentos ja gravados precisam ser reencontrados.
   */
  const reread = useMutation({
    mutationFn: (id: string) => api.rereadBankHistory(id),
    onSuccess: ({ run }) => {
      setError(null);
      setNotice(`Historico relido. ${resumoDaVarredura(run)}`);
      refresh();
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  const removeConnection = useMutation({
    mutationFn: (id: string) => api.deleteBankConnection(id),
    onSuccess: () => {
      setNotice('Conexao desligada. As contas e os lancamentos continuam aqui.');
      refresh();
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  const removeCredential = useMutation({
    mutationFn: (id: string) => api.deleteOpenFinanceCredential(id),
    onSuccess: refresh,
    onError: (caught) => setError(messageFor(caught)),
  });

  const lista = connections.data?.connections ?? [];
  const pares = credentials.data?.credentials ?? [];

  return (
    <div className="main">
      <header className="page-header">
        <div className="between">
          <div>
            <h1>Conexoes</h1>
            <p>
              Leitura automatica do extrato pelo Open Finance. O sistema so le: nao paga nada, nao
              transfere nada e nao guarda senha de banco.
            </p>
          </div>
          <button
            className="btn"
            onClick={() => setAddingConnection(!addingConnection)}
            disabled={pares.length === 0}
          >
            {addingConnection ? 'Cancelar' : 'Nova conexao'}
          </button>
        </div>
      </header>

      {error && (
        <div className="alert error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="alert success" role="status">
          {notice}
        </div>
      )}

      {addingConnection && (
        <ConnectionForm
          credentials={pares}
          onDone={(mensagem) => {
            setAddingConnection(false);
            setNotice(mensagem);
            refresh();
          }}
          onError={setError}
        />
      )}

      <section className="card">
        <div className="between">
          <h2 className="card-title">Conexoes</h2>
        </div>

        {connections.isLoading ? (
          <p className="muted">Carregando...</p>
        ) : lista.length === 0 ? (
          <p className="muted">
            Nenhuma conexao ainda. Cadastre uma credencial da Pluggy abaixo e depois registre o
            consentimento.
          </p>
        ) : (
          <div className="stack">
            {lista.map((connection) => (
              <ConnectionCard
                key={connection.id}
                connection={connection}
                syncing={sync.isPending && sync.variables === connection.id}
                onSync={() => sync.mutate(connection.id)}
                rereading={reread.isPending && reread.variables === connection.id}
                onReread={() => reread.mutate(connection.id)}
                onRemove={() => removeConnection.mutate(connection.id)}
                runsOpen={openRuns === connection.id}
                onToggleRuns={() => setOpenRuns(openRuns === connection.id ? null : connection.id)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="card">
        <div className="between">
          <div>
            <h2 className="card-title">Credenciais da Pluggy</h2>
            <p className="card-hint">
              Uma por pessoa: o plano gratuito admite ate cinco conexoes do mesmo titular. O segredo
              e gravado cifrado e nunca mais e exibido.
            </p>
          </div>
          <button className="btn ghost" onClick={() => setAddingCredential(!addingCredential)}>
            {addingCredential ? 'Cancelar' : 'Nova credencial'}
          </button>
        </div>

        {addingCredential && (
          <CredentialForm
            onDone={() => {
              setAddingCredential(false);
              setNotice('Credencial cadastrada e conferida na Pluggy.');
              refresh();
            }}
            onError={setError}
          />
        )}

        {pares.length === 0 ? (
          <p className="muted">
            Nenhuma credencial. Crie uma aplicacao em dashboard.pluggy.ai e cole aqui o Client ID e
            o Client Secret.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Apelido</th>
                  <th>Client ID</th>
                  <th>Dono</th>
                  <th>Conexoes</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pares.map((credential) => (
                  <tr key={credential.id}>
                    <td>{credential.nickname}</td>
                    <td className="mono muted">...{credential.clientIdTail}</td>
                    <td>{credential.ownerName ?? '-'}</td>
                    <td className="tabular">{credential.connectionCount}</td>
                    <td>
                      <button
                        className="btn ghost"
                        onClick={() => removeCredential.mutate(credential.id)}
                        disabled={credential.connectionCount > 0}
                        title={
                          credential.connectionCount > 0
                            ? 'Remova as conexoes que dependem desta credencial primeiro.'
                            : undefined
                        }
                      >
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ------------------------------------------------------------------ conexao

function ConnectionCard({
  connection,
  syncing,
  onSync,
  rereading,
  onReread,
  onRemove,
  runsOpen,
  onToggleRuns,
}: {
  connection: BankConnection;
  syncing: boolean;
  onSync: () => void;
  rereading: boolean;
  onReread: () => void;
  onRemove: () => void;
  runsOpen: boolean;
  onToggleRuns: () => void;
}) {
  const runs = useQuery({
    queryKey: ['openfinance', 'varreduras', connection.id],
    queryFn: () => api.syncRuns(connection.id),
    enabled: runsOpen,
  });

  return (
    <article className="card">
      <div className="between">
        <div>
          <h3 className="card-title">{connection.institutionName}</h3>
          <p className="card-hint">
            Credencial {connection.credentialNickname} &middot; {connection.accountCount} conta(s)
          </p>
        </div>
        <StatusBadge connection={connection} />
      </div>

      <ConsentWarning connection={connection} />

      <p className="muted">
        {connection.lastSyncedAt
          ? `Ultima varredura em ${dataHora(connection.lastSyncedAt)}.`
          : 'Ainda nao foi varrida.'}
        {connection.lastRun && ` ${resumoDaVarredura(connection.lastRun)}`}
      </p>

      {connection.lastRun?.pagesExhausted && (
        <div className="alert warning" role="alert">
          A varredura parou no teto de paginas e nao chegou ao fim. Nada foi perdido: o ponto de
          retomada nao avancou, e a proxima execucao continua de onde esta.
        </div>
      )}

      {connection.lastRun?.status === 'FAILED' && (
        <div className="alert error" role="alert">
          {connection.lastRun.errorText ?? 'A ultima varredura falhou.'}
        </div>
      )}

      <div className="row">
        <button className="btn" onClick={onSync} disabled={syncing}>
          {syncing ? 'Sincronizando...' : 'Sincronizar agora'}
        </button>
        <button
          className="btn ghost"
          onClick={onReread}
          disabled={rereading}
          title="Pede a janela inteira de novo, para preencher campos que antes nao eram guardados. Nao duplica nada."
        >
          {rereading ? 'Relendo...' : 'Reler historico'}
        </button>
        <button className="btn ghost" onClick={onToggleRuns}>
          {runsOpen ? 'Ocultar historico' : 'Historico'}
        </button>
        <button className="btn danger" onClick={onRemove}>
          Desligar
        </button>
      </div>

      {runsOpen && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Origem</th>
                <th>Situacao</th>
                <th>Vieram</th>
                <th>Novos</th>
                <th>Ja existiam</th>
                <th>Sem traducao</th>
              </tr>
            </thead>
            <tbody>
              {(runs.data?.runs ?? []).map((run) => (
                <tr key={run.id}>
                  <td>{dataHora(run.startedAt)}</td>
                  <td>{ORIGEM[run.trigger]}</td>
                  <td>{SITUACAO[run.status]}</td>
                  <td className="tabular">{run.fetchedCount}</td>
                  <td className="tabular">{run.createdCount}</td>
                  <td className="tabular">{run.skippedCount}</td>
                  <td className="tabular">{run.unmappedCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

const ORIGEM: Record<SyncRun['trigger'], string> = {
  SCHEDULE: 'Agendada',
  MANUAL: 'Manual',
  WEBHOOK: 'Aviso do banco',
};

const SITUACAO: Record<SyncRun['status'], string> = {
  RUNNING: 'Em andamento',
  SUCCESS: 'Concluida',
  FAILED: 'Falhou',
};

const ESTADO: Record<BankConnection['status'], { rotulo: string; classe: string }> = {
  ACTIVE: { rotulo: 'Ativa', classe: 'badge accent' },
  WAITING_USER: { rotulo: 'Aguardando voce', classe: 'badge' },
  CONSENT_EXPIRED: { rotulo: 'Consentimento vencido', classe: 'badge' },
  ERROR: { rotulo: 'Com erro', classe: 'badge' },
};

function StatusBadge({ connection }: { connection: BankConnection }) {
  const estado = ESTADO[connection.status];
  return <span className={estado.classe}>{estado.rotulo}</span>;
}

/**
 * O consentimento do Open Finance vale ate 12 meses e expira sem avisar: a
 * conexao simplesmente para de atualizar. Avisar com antecedencia e a unica
 * coisa que transforma isso num aborrecimento em vez de numa surpresa.
 */
function ConsentWarning({ connection }: { connection: BankConnection }) {
  const dias = connection.consentDaysLeft;
  if (dias === null) return null;

  if (dias < 0) {
    return (
      <div className="alert error" role="alert">
        O consentimento venceu. Autorize de novo na Pluggy e registre o novo itemId aqui — ele muda
        a cada consentimento.
      </div>
    );
  }

  if (dias <= 30) {
    return (
      <div className="alert warning" role="alert">
        O consentimento vence em {dias} dia(s). Depois disso a conexao para de atualizar sem avisar.
      </div>
    );
  }

  return <p className="muted">Consentimento valido por mais {dias} dia(s).</p>;
}

// ----------------------------------------------------------------- formularios

function CredentialForm({
  onDone,
  onError,
}: {
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [nickname, setNickname] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const create = useMutation({
    mutationFn: () => api.createOpenFinanceCredential({ nickname, clientId, clientSecret }),
    onSuccess: () => {
      setClientSecret('');
      onDone();
    },
    onError: (caught) => onError(messageFor(caught)),
  });

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        onError('');
        create.mutate();
      }}
    >
      <div className="grid">
        <label className="field">
          <span>Apelido</span>
          <input
            value={nickname}
            onChange={(event) => setNickname(event.target.value)}
            placeholder="Victor"
            required
          />
        </label>
        <label className="field">
          <span>Client ID</span>
          <input
            value={clientId}
            onChange={(event) => setClientId(event.target.value)}
            autoComplete="off"
            required
          />
        </label>
        <label className="field">
          <span>Client Secret</span>
          <input
            type="password"
            value={clientSecret}
            onChange={(event) => setClientSecret(event.target.value)}
            autoComplete="new-password"
            required
          />
          <small className="field-hint">
            Gravado cifrado e nunca mais exibido. Para trocar, cadastre outra credencial.
          </small>
        </label>
      </div>
      <button className="btn" type="submit" disabled={create.isPending}>
        {create.isPending ? 'Conferindo na Pluggy...' : 'Cadastrar'}
      </button>
    </form>
  );
}

function ConnectionForm({
  credentials,
  onDone,
  onError,
}: {
  credentials: Array<{ id: string; nickname: string }>;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [chosen, setChosen] = useState('');
  const [itemId, setItemId] = useState('');
  const [institutionName, setInstitutionName] = useState('');

  // A escolha e derivada, e nao um estado que nasceu antes dos dados: fixar o
  // primeiro item num `useState` inicial deixa o formulario mostrando uma
  // credencial enquanto o estado ainda esta vazio.
  const credentialId = credentials.some((c) => c.id === chosen)
    ? chosen
    : (credentials[0]?.id ?? '');

  const create = useMutation({
    mutationFn: () =>
      api.createBankConnection({
        credentialId,
        itemId: itemId.trim(),
        ...(institutionName.trim() ? { institutionName: institutionName.trim() } : {}),
      }),
    onSuccess: (connection) => onDone(`Conexao com ${connection.institutionName} registrada.`),
    onError: (caught) => onError(messageFor(caught)),
  });

  return (
    <section className="card">
      <h2 className="card-title">Nova conexao</h2>
      <p className="card-hint">
        Autorize o banco na Pluggy e cole aqui o <strong>itemId</strong> que ela devolve. Ele
        precisa ser colado a mao porque a listagem de consentimentos nao esta liberada no plano
        gratuito — e ele muda a cada nova autorizacao.
      </p>

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          onError('');
          create.mutate();
        }}
      >
        <div className="grid">
          <label className="field">
            <span>Credencial</span>
            <select value={credentialId} onChange={(event) => setChosen(event.target.value)}>
              {credentials.map((credential) => (
                <option key={credential.id} value={credential.id}>
                  {credential.nickname}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>itemId</span>
            <input
              value={itemId}
              onChange={(event) => setItemId(event.target.value)}
              placeholder="24aae9cb-122d-4742-a7f0-209d6536e22d"
              required
            />
          </label>
          <label className="field">
            <span>Nome da instituicao (opcional)</span>
            <input
              value={institutionName}
              onChange={(event) => setInstitutionName(event.target.value)}
              placeholder="vem da Pluggy se ficar em branco"
            />
          </label>
        </div>
        <button className="btn" type="submit" disabled={create.isPending || !credentialId}>
          {create.isPending ? 'Conferindo o consentimento...' : 'Registrar'}
        </button>
      </form>
    </section>
  );
}

// ------------------------------------------------------------------- apoio

/**
 * A prestacao de contas de uma varredura, em uma frase.
 *
 * Os quatro numeros aparecem juntos de proposito: sozinho, "0 novos" nao
 * distingue "o banco nao tinha nada" de "chegaram trinta e nenhum deu para
 * traduzir".
 */
function resumoDaVarredura(run: SyncRun): string {
  if (run.status === 'FAILED') return 'A ultima tentativa falhou.';

  const partes = [`${run.fetchedCount} lancamento(s) vieram`, `${run.createdCount} novo(s)`];
  if (run.skippedCount > 0) partes.push(`${run.skippedCount} ja existia(m)`);
  if (run.transferCount > 0) partes.push(`${run.transferCount} transferencia(s) casada(s)`);
  if (run.unmappedCount > 0) partes.push(`${run.unmappedCount} sem traducao`);

  return `${partes.join(', ')}.`;
}

function dataHora(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(iso));
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) return caught.message;
  return 'Nao foi possivel completar a operacao.';
}
